// -------------------------------------------- IMPORT FILES START --------------------------------------------------------------
// ## Import Files
import $api from "../Api.js";
import ResponseChecker from "../Middlewares/ResponseChecker.js";
import { defaultMessages } from "../enums/responseMessages.js";
import Endpoints from "../Endpoints/index.js";

// -------------------------------------------- IMPORT FILES ENDED --------------------------------------------------------------

/**
 * @typedef {object} AverageStatisticResult
 * @property {string} startDate
 * @property {string} endDate
 * @property {number} kdvAmount
 * @property {number} totalAmount
 * @property {number} totalAmountWithoutKDV
 * @property {number} saleCount
 * @property {number} averageMonthCount
 */

/**
 * @typedef {object} SaleStatisticCore
 * @property {string} startDate
 * @property {string} endDate
 * @property {number} kdvAmount
 * @property {number} totalAmount
 * @property {number} totalAmountWithoutKDV
 * @property {number} saleCount
 * @property {string[]=} saleIds
 * @property {AverageStatisticResult} averageStatistic
 */

/**
 * @typedef {{ count: number, items: T[] }} PaginatedList
 * @template T
 */

/**
 * @typedef {{ message: string, isSuccess?: boolean, data?: T }} StatisticResponse
 * @template T
 */

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { employeeInfo: object }>>>}
 */
export const GetSaleStatisticEmployees = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticEmployees(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { employeeInfo: object }>>>}
 */
export const GetSaleStatisticEmployeesMonthly = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticEmployeesMonthly(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { productCategoryInfo: object }>>>}
 */
export const GetSaleStatisticProductCategories = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticProductCategories(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { productCategoryInfo: object }>>>}
 */
export const GetSaleStatisticProductCategoriesMonthly = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticProductCategoriesMonthly(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { productInfo: object }>>>}
 */
export const GetSaleStatisticProducts = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticProducts(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { productInfo: object }>>>}
 */
export const GetSaleStatisticProductsMonthly = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticProductsMonthly(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { regionInfo: object }>>>}
 */
export const GetSaleStatisticRegions = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticRegions(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { regionInfo: object }>>>}
 */
export const GetSaleStatisticRegionsMonthly = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticRegionsMonthly(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { locationInfo: object }>>>}
 */
export const GetSaleStatisticLocations = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticLocations(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { locationInfo: object }>>>}
 */
export const GetSaleStatisticLocationsMonthly = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticLocationsMonthly(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore>>>}
 */
export const GetSaleStatistics = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatistics(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore>>>}
 */
export const GetSaleStatisticsMonthly = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticsMonthly(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<{ outstandingAmount: number, overdueAmount: number }>>}
 */
export const GetTransactionSaleResults = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetTransactionSaleResults(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { customerInfo: object }>>>}
 */
export const GetSaleStatisticCustomers = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticCustomers(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{params?: object, query?: object}} option
 * @returns {Promise<StatisticResponse<PaginatedList<SaleStatisticCore & { customerInfo: object }>>>}
 */
export const GetSaleStatisticCustomersMonthly = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetSaleStatisticCustomersMonthly(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{
 *   params?: object,
 *   query: {
 *     startDate: string,
 *     endDate: string,
 *     employeeId?: string,
 *     productId?: string,
 *     companyId?: string,
 *     search?: string,
 *     page?: number,
 *     limit?: number,
 *     sortFilter?: string,
 *     sortType?: string
 *   }
 * }} option
 * @returns {Promise<StatisticResponse<{
 *   list: PaginatedList<{
 *     employeeId?: string,
 *     employee?: string,
 *     productId?: string,
 *     product?: string,
 *     productCode?: string,
 *     isOneTimeProduct: boolean,
 *     quantity: number,
 *     profit: number,
 *     totalAmountWithoutKDV: number,
 *     kdvAmount: number,
 *     saleIds?: string[]
 *   }>,
 *   profit: number,
 *   totalAmountWithoutKDV: number,
 *   kdvAmount: number
 * }>>}
 */
export const GetEmployeeProductProfitStatistics = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetEmployeeProductProfitStatistics(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);
  return checker;
};

/**
 * @param {{ params?: object, query?: object }} option
 * @returns {Promise<Blob|string>}
 */
export const GetEmployeeProductProfitStatisticsExcel = async (option) => {
  return $api.blob(
    Endpoints.Statistic.GetEmployeeProductProfitStatisticsExcel(option)
  );
};

/**
 * @param {{
 *   params?: object,
 *   query?: {
 *     StartDate?: string,
 *     EndDate?: string,
 *     LocationId?: string,
 *     TagIds?: string,
 *     CustomerTagFilterType?: string,
 *     Search?: string,
 *   }
 * }} option
 * @returns {Promise<StatisticResponse<Array<{
 *   totaleSaleProductCount: number,
 *   totalSaleCount: number,
 *   totalAmount: number,
 *   lastSaleDate?: string,
 *   tagList?: string[],
 *   customerFullname?: string,
 *   customerShortName?: string,
 *   customerCompanyName?: string
 * }>>>}
 */
export const GetCustomerSaleStatistics = async (option) => {
  const response = await $api.get(
    Endpoints.Statistic.GetCustomerSaleStatistics(option)
  );
  const checker = await ResponseChecker(defaultMessages, response);

  if (checker.message === "OK") {
    return checker;
  }

  if (response?.isSuccess && Array.isArray(response?.data)) {
    return {
      ...response,
      message: "OK",
    };
  }

  return checker;
};

export default {
  GetSaleStatisticEmployees,
  GetSaleStatisticProductCategories,
  GetSaleStatisticProducts,
  GetSaleStatisticRegions,
  GetSaleStatistics,
  GetTransactionSaleResults,
  GetSaleStatisticCustomers,
  GetSaleStatisticCustomersMonthly,
  GetSaleStatisticEmployeesMonthly,
  GetSaleStatisticProductCategoriesMonthly,
  GetSaleStatisticProductsMonthly,
  GetSaleStatisticRegionsMonthly,
  GetSaleStatisticLocations,
  GetSaleStatisticLocationsMonthly,
  GetSaleStatisticsMonthly,
  GetEmployeeProductProfitStatistics,
  GetEmployeeProductProfitStatisticsExcel,
  GetCustomerSaleStatistics,
};
